// Runtime game configuration loaded from Firestore.
//
// All game content (courses, shop items, activities, positions, ...) lives in the
// `gameConfig` collection, one document per domain holding an `items` array. This
// module loads it once during startup and caches it, so the accessor functions in
// src/data can stay synchronous and their ~50 call sites don't need to become async.
//
// Since 55.97 (ROADMAP 2.3) admin edits reach the pages live: every accepted change bumps a version that
// useGameConfigVersion() hands to React, the app root re-renders on it, and the memos that read config recompute.
//
// Since 55.100 (ROADMAP 2.11) the browser keeps what it read in localStorage and, at startup, reads ONE document —
// `gameConfig/_versions`, the update time of every domain, kept by the bumpConfigVersion trigger
// (functions/src/configVersions.ts) — and fetches only the domains whose version moved. A returning player whose
// configuration has not changed costs one read instead of twenty-one; the same listener on that one document carries
// the live edits. Without it (a fresh database, before the trigger has run) the old listener on the domains does both.
import { DependencyList, useState, useSyncExternalStore } from 'react';
import { collection, doc, documentId, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from './firebase';

// The domains the browser reads. Only these are fetched: the query below names them, so a document
// in `gameConfig` the client does not use — the server's `events`, the department's
// `departmentUnitUpgrades` kept for its rebuild — is never downloaded.
// Until October 2026 the whole collection was fetched at every startup, retired documents
// included. A domain on this list is fetched whether anything reads it or not.
export const GAME_CONFIG_DOMAINS = [
    'courses',
    'shopItems',
    'abilities',
    'policePositions',
    'positionWorkActivities',
    'trainingActivities',
    'prefectures',
    'monthlyTopPlayers',
    'casinoMonthlyRewards',
    'moderation',
    'shopCategories',
    'courseCategories',
    'economy',
    'salaryRates',
    'workBonuses',
    'worlds',
    'stocks',
    // Production (55.55): what the jobs make, and the jobs. tools/apply-production.mjs writes both.
    'materials',
    'productionJobs',
    // The plot's rooms (55.57), also written by tools/apply-production.mjs.
    'homeRooms',
    // The six talitust every jaoskond has, for the department page (55.78).
    'departmentUnits',
] as const;

export type GameConfigDomain = typeof GAME_CONFIG_DOMAINS[number];

/** Domains keyed by something other than `id`, so exempt from the unique-id check. */
const DOMAINS_WITHOUT_IDS: ReadonlySet<GameConfigDomain> = new Set([
    'monthlyTopPlayers',     // keyed by month/year
    'casinoMonthlyRewards',  // keyed by `position`
]);

/**
 * Domains the owner may empty from the admin panel. The shop's catalogue is edited in Admin → Pood
 * (55.45), and a shop with nothing on sale is a legitimate state, not missing game data.
 */
const DOMAINS_MAY_BE_EMPTY: ReadonlySet<GameConfigDomain> = new Set([
    'shopItems',
    'shopCategories',
]);

const COLLECTION = 'gameConfig';

let cache: Partial<Record<GameConfigDomain, unknown[]>> = {};
let unsubscribe: (() => void) | null = null;
const listeners = new Set<() => void>();
/** Bumped on every accepted change after the first load; what React re-renders on. */
let version = 0;

/**
 * Validates one domain payload. Compile-time literal unions were traded away so game
 * data could live entirely in the database, so this is where bad data is caught —
 * loudly, at startup, instead of silently corrupting progression or currency logic.
 */
function validateDomain(domain: GameConfigDomain, items: unknown): asserts items is unknown[] {
    if (!Array.isArray(items)) {
        throw new Error(`gameConfig/${domain}: "items" is ${typeof items}, expected an array.`);
    }
    if (items.length === 0 && !DOMAINS_MAY_BE_EMPTY.has(domain)) {
        throw new Error(`gameConfig/${domain}: "items" is empty. Refusing to run with missing game data.`);
    }
    if (DOMAINS_WITHOUT_IDS.has(domain)) return;

    const seen = new Set<string>();
    items.forEach((item, index) => {
        if (typeof item !== 'object' || item === null) {
            throw new Error(`gameConfig/${domain}: item at index ${index} is not an object.`);
        }
        const id = (item as { id?: unknown }).id;
        if (typeof id !== 'string' || id.length === 0) {
            throw new Error(`gameConfig/${domain}: item at index ${index} has no usable string "id".`);
        }
        if (seen.has(id)) {
            throw new Error(`gameConfig/${domain}: duplicate id "${id}".`);
        }
        seen.add(id);
    });
}

/** Validates a complete set of domains and makes it the cache; throws, leaving the cache as it was, if it is not. */
function ingest(domains: Partial<Record<string, unknown>>): void {
    const next: Partial<Record<GameConfigDomain, unknown[]>> = {};
    for (const domain of GAME_CONFIG_DOMAINS) {
        const items = domains[domain];
        if (items === undefined) continue;
        validateDomain(domain, items);
        next[domain] = items;
    }

    const missing = GAME_CONFIG_DOMAINS.filter((d) => !next[d]);
    if (missing.length) {
        throw new Error(
            `gameConfig is incomplete — missing document(s): ${missing.join(', ')}. ` +
            `Run "node tools/seed-game-config.mjs --write" to populate them.`
        );
    }
    cache = next;
}

const itemsOf = (docs: { id: string; data: () => Record<string, unknown> }[]): Record<string, unknown> =>
    Object.fromEntries(docs.map((d) => [d.id, d.data().items]));

// At most 30 ids in an `in` filter; the list is 21.
const domainsQuery = (domains: readonly string[] = GAME_CONFIG_DOMAINS) =>
    query(collection(firestore, COLLECTION), where(documentId(), 'in', [...domains]));

// ---- The browser's copy (55.100) ----------------------------------------------------------------

/** The version list: `{ domains: { courses: 1760950000123456, ... } }`, microseconds, only ever raised. */
const VERSIONS_DOC = '_versions';

/** One copy per Firebase project, so the emulator's data and production's never mix. A new shape gets a new key. */
const storeKey = () => `vomm.gameConfig.v1.${firestore.app.options.projectId ?? 'default'}`;

interface Stored {
    versions: Record<string, number>;
    items: Partial<Record<string, unknown>>;
}

const EMPTY: Stored = { versions: {}, items: {} };

/** The stored copy, or an empty one: a private window, blocked storage and a damaged entry all read as nothing kept. */
function readStore(): Stored {
    try {
        const raw = JSON.parse(localStorage.getItem(storeKey()) ?? 'null');
        if (raw && typeof raw === 'object' && raw.versions && typeof raw.versions === 'object' && raw.items && typeof raw.items === 'object') {
            return { versions: raw.versions, items: raw.items };
        }
    } catch { /* nothing kept */ }
    return EMPTY;
}

function writeStore(stored: Stored): void {
    try {
        localStorage.setItem(storeKey(), JSON.stringify(stored));
    } catch {
        // Full or blocked: the next start simply reads more. Drop an old copy rather than trust it.
        try { localStorage.removeItem(storeKey()); } catch { /* blocked */ }
    }
}

/** The domains to fetch: never read, read at another version than the list now names, or not on the list at all. */
function staleDomains(stored: Stored, remote: Record<string, unknown>): GameConfigDomain[] {
    return GAME_CONFIG_DOMAINS.filter((d) =>
        stored.items[d] === undefined || typeof remote[d] !== 'number' || stored.versions[d] !== remote[d]);
}

// ---- Loading ---------------------------------------------------------------------------------------

/** The first load, shared: a second call (React's StrictMode runs the startup effect twice) waits for the same snapshot. */
let loading: Promise<void> | null = null;

export function loadGameConfig(): Promise<void> {
    loading ??= openListener();
    return loading;
}

/** A change after the first load: React hears of it. */
function announce(): void {
    version += 1;
    listeners.forEach((fn) => fn());
}

function openListener(): Promise<void> {
    // ONE listener, on the version list. Its first snapshot is the load: the domains whose version moved (all of them
    // on a first visit) are fetched, the rest come from the browser's copy, and the whole set must validate — a bad
    // catalogue stops the app before it renders. Later snapshots are admin edits: the same, but a bad set is logged
    // rather than thrown, so a bad edit cannot crash live sessions already running on known-good data.
    return new Promise<void>((resolve, reject) => {
        let loaded = false;
        let stored = readStore();
        // One refresh at a time, in the order the snapshots came: a quick second edit waits for the first's fetch.
        let queue: Promise<void> = Promise.resolve();

        const fail = (error: unknown) => {
            unsubscribe?.();
            unsubscribe = null;
            loading = null;
            reject(error);
        };

        const refresh = async (remote: Record<string, unknown>) => {
            const stale = staleDomains(stored, remote);
            if (loaded && stale.length === 0) return;
            const fetched = stale.length ? itemsOf((await getDocs(domainsQuery(stale))).docs) : {};
            if (import.meta.env.DEV) console.debug(`gameConfig: ${stale.length}/${GAME_CONFIG_DOMAINS.length} domains fetched`, stale);
            const items = { ...stored.items, ...fetched };
            try {
                ingest(items);
            } catch (error) {
                if (!loaded) {
                    // Perhaps the copy is what is wrong: forget it and read everything once before giving up.
                    writeStore(EMPTY);
                    if (stale.length < GAME_CONFIG_DOMAINS.length) {
                        stored = EMPTY;
                        return refresh(remote);
                    }
                    throw error;
                }
                console.error('Rejected a gameConfig update; keeping previous values.', error);
                return;
            }
            // A version is recorded only with what was fetched after the list named it, so the copy is never older
            // than its label; a domain the list does not name yet stays unlabelled and is fetched again next time.
            const versions = { ...stored.versions };
            for (const d of stale) {
                if (typeof remote[d] === 'number') versions[d] = remote[d] as number;
                else delete versions[d];
            }
            stored = { versions, items };
            writeStore(stored);
            if (loaded) announce();
        };

        unsubscribe = onSnapshot(
            doc(firestore, COLLECTION, VERSIONS_DOC),
            (snap) => {
                if (!snap.exists()) {
                    // No version list in this database (yet): the domains' own listener carries the load and the edits.
                    if (!loaded) {
                        loaded = true;
                        unsubscribe?.();
                        openDomainsListener().then(resolve, (error) => { loading = null; reject(error); });
                    }
                    return;
                }
                const remote = (snap.data().domains ?? {}) as Record<string, unknown>;
                queue = queue.then(() => refresh(remote)).then(
                    () => { if (!loaded) { loaded = true; resolve(); } },
                    (error) => {
                        if (!loaded) fail(error);
                        else console.error('gameConfig refresh failed; keeping previous values.', error);
                    },
                );
            },
            (error) => {
                console.error('gameConfig version listener failed:', error);
                if (!loaded) fail(error);
            }
        );
    });
}

/** Before 55.100, and still where no version list exists: one listener on the domains themselves, no browser copy. */
function openDomainsListener(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        let loaded = false;
        unsubscribe = onSnapshot(
            domainsQuery(),
            (snap) => {
                try {
                    ingest(itemsOf(snap.docs));
                } catch (error) {
                    if (!loaded) {
                        unsubscribe?.();
                        unsubscribe = null;
                        reject(error);
                    } else {
                        console.error('Rejected a gameConfig update; keeping previous values.', error);
                    }
                    return;
                }
                if (loaded) announce();
                else { loaded = true; resolve(); }
            },
            (error) => {
                console.error('gameConfig snapshot listener failed:', error);
                if (!loaded) { unsubscribe = null; reject(error); }
            }
        );
    });
}

/** Subscribes to config changes. Returns an unsubscribe function. */
export function onGameConfigChange(fn: () => void): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

/**
 * The config's version, for React: a component that calls this re-renders when an admin edit lands. The app root
 * calls it, so the whole tree re-renders then (a rare event — an edit in the admin panel); a memo that reads config
 * names it, and uses it (`void configVersion`), so it recomputes too.
 */
export function useGameConfigVersion(): number {
    return useSyncExternalStore(onGameConfigChange, () => version);
}

/**
 * A value read from config, memoised on `deps` AND on the config's version: `useConfigValue(getStocks)`,
 * `useConfigValue(() => buildStructure(roster), [roster])`. What a `useMemo(() => getX(), [])` was, minus the staleness.
 * The caller lists the deps its reader uses, as with useMemo.
 */
export function useConfigValue<T>(read: () => T, deps: DependencyList = []): T {
    const configVersion = useGameConfigVersion();
    const [memo, setMemo] = useState(() => ({ configVersion, deps, value: read() }));
    // Recomputed during render when the version or a dep moved — React's pattern for state derived from changed input.
    if (memo.configVersion !== configVersion || memo.deps.length !== deps.length || memo.deps.some((d, i) => !Object.is(d, deps[i]))) {
        const next = { configVersion, deps, value: read() };
        setMemo(next);
        return next.value;
    }
    return memo.value;
}

/** Reads one domain from the cache. Throws if called before loadGameConfig() resolves. */
export function getDomain<T>(domain: GameConfigDomain): T[] {
    const items = cache[domain];
    if (!items) {
        throw new Error(
            `Game config domain "${domain}" was read before loadGameConfig() completed. ` +
            `Game data must not be accessed at module scope.`
        );
    }
    return items as T[];
}
